import {
  ComponentIds,
  Vector,
  Entity,
  InnerMeasurement,
  SelectedComponent,
  MovedToValidSnapPositionComponent,
  Ray,
  WorldTransformComponent,
  RaycastUtils,
  CommunicationSizeComponent
} from '@inter-ikea-core/core';
import { CoreApi } from '../CoreApi';
import { PlannerEcsMultiSystem } from './System';
import {
  InnerLineCenterPoint3D,
  DistanceLineCenterComponent,
  DistanceMeasurementComponent
} from '../..';
import { IsDraggedComponent } from '@inter-ikea-core/core';
import EventComponent from '../components/EventComponent';
import { getInnerLineCenterPoints, getLineCenters } from '../../utils/measurementUtils';
import { Direction } from '../components/DistanceMeasurementComponent';
import { IViewer } from '@inter-ikea-betrakta/viewer';
import { LineMeasureWidget } from '@inter-ikea-betrakta/viewer/dist/modules/MeasurementWidget';

const scale = 1000;
const color = { r: 21 / 255, g: 146 / 255, b: 230 / 255 };
const directionToTop = new Vector(0, 1, 0);
const directionToBottom = new Vector(0, -1, 0);
const directionToLeft = new Vector(-1, 0, 0);
const directionToRight = new Vector(1, 0, 0);
const directionHorizontal = new Vector(0, 0, 0);

const directionMap = new Map();
directionMap.set(Direction.UP, directionToTop);
directionMap.set(Direction.DOWN, directionToBottom);
directionMap.set(Direction.LEFT, directionToLeft);
directionMap.set(Direction.RIGHT, directionToRight);

/**
 * Converts Core values to Viewer values.
 * Ex: Core uses milimeters to calculate, Viwer meters. Core uses X Y Z, Viewer x, y, z.
 */
const getLine = ({ distance, start, end }: InnerMeasurement, direction: Vector) => ({
  start: {
    x: start.point.X / scale,
    y: start.point.Y / scale,
    z: start.point.Z / scale
  },
  end: {
    x: end.point.X / scale,
    y: end.point.Y / scale,
    z: end.point.Z / scale
  },
  distance: distance,
  direction: { x: direction.X, y: direction.Y, z: direction.Z },
  color: color,
  thickness: 0.01,
  capWidth: 0
});

type Line = LineMeasureWidget;

type LineMap = {
  [id: string]: Line;
};

type SubscribedComponents =
  | SelectedComponent
  | MovedToValidSnapPositionComponent
  | IsDraggedComponent
  | EventComponent
  | DistanceMeasurementComponent;

export class DistanceMeasurementSystem extends PlannerEcsMultiSystem<SubscribedComponents> {
  coreApi: CoreApi;
  lines: LineMap;
  lineCenters: InnerLineCenterPoint3D[];
  measurementTop: InnerMeasurement | null;
  measurementBottom: InnerMeasurement | null;
  measurementLeft: InnerMeasurement | null;
  measurementRight: InnerMeasurement | null;
  measurementsEntity: Entity;

  constructor(coreApi: CoreApi) {
    super(coreApi, 'DistanceMeasurementSystem', [
      ComponentIds.SELECTED_COMPONENT_ID,
      ComponentIds.MOVED_TO_VALID_SNAP_POSITION_COMPONENT_ID,
      ComponentIds.IS_DRAGGED_COMPONENT_ID,
      EventComponent.id,
      DistanceMeasurementComponent.id
    ]);
    this.coreApi = coreApi;
    this.lines = {};
    this.lineCenters = [];
    this.measurementTop = null;
    this.measurementBottom = null;
    this.measurementLeft = null;
    this.measurementRight = null;
    this.measurementsEntity = coreApi.getServices().entityManager.createEntity();
  }

  enable(): void {
    this.isEnabled = true;
    this.updateLines();
  }

  disable(): void {
    this.isEnabled = false;
    this.onRemoveLines();
  }

  onAdd(entity: Entity, newComponent: SubscribedComponents): void {
    if (!this.isEnabled || this.coreApi.history.isHistoryActionOngoing()) {
      return;
    }
    switch (newComponent.typeId) {
      case ComponentIds.SELECTED_COMPONENT_ID:
        this.updateLines(entity);
        break;
      case ComponentIds.MOVED_TO_VALID_SNAP_POSITION_COMPONENT_ID:
        if (entity.hasComponent(ComponentIds.IN_SCENE_COMPONENT_ID)) {
          this.updateLines(entity);
        }
        break;
      case DistanceMeasurementComponent.id:
        if (
          entity.hasComponent(ComponentIds.SELECTED_COMPONENT_ID) ||
          entity.hasComponent(ComponentIds.IS_DRAGGED_COMPONENT_ID)
        ) {
          this.updateLines(entity);
        }
        break;
    }
  }
  onUpdate(
    entity: Entity,
    oldComponent: SubscribedComponents,
    newComponent: SubscribedComponents
  ): void {
    if (!this.isEnabled) {
      return;
    }
    switch (newComponent.typeId) {
      case EventComponent.id:
        const component = newComponent as EventComponent;
        if (component.event === 'update') {
          this.setLineCenters();
        } else if (component.event === 'undo' || component.event === 'redo') {
          this.onRemoveLines();
        } else if (component.event === 'history') {
          this.updateLines();
        }
        break;
      case DistanceMeasurementComponent.id:
        if (
          entity.hasComponent(ComponentIds.SELECTED_COMPONENT_ID) ||
          entity.hasComponent(ComponentIds.IS_DRAGGED_COMPONENT_ID)
        ) {
          this.updateLines(entity);
        }

        break;
      default:
        break;
    }
  }

  onRemove(entity: Entity, oldComponent: SubscribedComponents): void {
    if (!this.isEnabled) {
      return;
    }
    switch (oldComponent.typeId) {
      case ComponentIds.SELECTED_COMPONENT_ID:
        if (entity.hasComponent(DistanceMeasurementComponent.id)) {
          this.onRemoveLines();
        }
        break;
      case ComponentIds.IS_DRAGGED_COMPONENT_ID:
        if (
          entity.hasComponent(DistanceMeasurementComponent.id) &&
          !entity.hasComponent(ComponentIds.SELECTED_COMPONENT_ID)
        ) {
          this.onRemoveLines();
        }
        break;
      case DistanceMeasurementComponent.id:
        if (entity.hasComponent(ComponentIds.IN_SCENE_COMPONENT_ID)) {
          this.onRemoveLines();
        }
        break;
    }
  }

  /**
   * Gets the distance measurement for an entity in a given direction
   */
  getDistanceMeasurement(entity: Entity, direction: Direction) {
    const sceneEntity = this.coreApi.entities.getScene();
    const distanceMeasurementComponent = entity.getComponent<DistanceMeasurementComponent>(
      DistanceMeasurementComponent.id
    );
    if (
      !sceneEntity ||
      !distanceMeasurementComponent ||
      !distanceMeasurementComponent.directions.includes(direction)
    ) {
      return null;
    }

    const measurement = this.getMeasurementToNextEntity(
      entity,
      sceneEntity,
      direction,
      distanceMeasurementComponent.measureCloserToWall
    );
    if (measurement && measurement.distance < 5) {
      // Don't show measuremens less than 5mm
      return null;
    }
    if (
      distanceMeasurementComponent.omitIfRoom.includes(direction) &&
      measurement?.end.entity.hasComponent(ComponentIds.ROOM_PART_COMPONENT_ID)
    ) {
      // If distance is measuremed to room and DistanceMeasurementComponent holds omitIfRoom in that direction we don't show it
      return null;
    }
    if (direction === Direction.DOWN && this.isPlacedOnFloor(entity)) {
      // Don't show distance measurement down if floorstanding entity
      return null;
    }
    return measurement;
  }

  isPlacedOnFloor(entity: Entity): boolean {
    const movedToValidSnap = entity.getComponent<MovedToValidSnapPositionComponent>(
      ComponentIds.MOVED_TO_VALID_SNAP_POSITION_COMPONENT_ID
    );
    if (movedToValidSnap) {
      const guestConnector = movedToValidSnap.snap.guestConnector;
      return (
        guestConnector.demands().includes('wall-floor-edge') ||
        guestConnector.demands().includes('floor')
      );
    }
    return this.coreApi.entities.isEntityPlacedOnFloor(entity);
  }

  /**
   * Update measurement lines for entity
   */
  updateLines(entity?: Entity) {
    const selectedEntity = entity || this.coreApi.entities.findSelectedEntity();
    if (
      selectedEntity &&
      selectedEntity.hasComponent(DistanceMeasurementComponent.id) &&
      !selectedEntity.getComponent<DistanceMeasurementComponent>(DistanceMeasurementComponent.id)
        ?.disabled
    ) {
      this.onRemoveLines();
      this.getLines(selectedEntity);
    }
  }

  /**
   * Creates and updates the measurement lines
   * to the nearest interior above and below in a frame.
   */
  getLines(entity: Entity) {
    const viewer = this.coreApi.ipexViewer as IViewer;

    this.measurementTop = this.getDistanceMeasurement(entity, Direction.UP);
    this.measurementBottom = this.getDistanceMeasurement(entity, Direction.DOWN);
    this.measurementLeft = this.getDistanceMeasurement(entity, Direction.LEFT);
    this.measurementRight = this.getDistanceMeasurement(entity, Direction.RIGHT);

    if (this.measurementTop) {
      this.lines.lineTop = new LineMeasureWidget(
        viewer,
        getLine(this.measurementTop, directionHorizontal).start,
        getLine(this.measurementTop, directionHorizontal).end,
        color,
        0.01,
        getLine(this.measurementTop, directionHorizontal).direction,
        0.01
      );
    }

    if (this.measurementBottom) {
      this.lines.lineBottom = new LineMeasureWidget(
        viewer,
        getLine(this.measurementBottom, directionHorizontal).start,
        getLine(this.measurementBottom, directionHorizontal).end,
        color,
        0.01,
        getLine(this.measurementBottom, directionHorizontal).direction,
        0.01
      );
    }

    if (this.measurementLeft) {
      this.lines.lineLeft = new LineMeasureWidget(
        viewer,
        getLine(this.measurementLeft, directionHorizontal).start,
        getLine(this.measurementLeft, directionHorizontal).end,
        color,
        0.01,
        getLine(this.measurementLeft, directionHorizontal).direction,
        0.01
      );
    }

    if (this.measurementRight) {
      this.lines.lineRight = new LineMeasureWidget(
        viewer,
        getLine(this.measurementRight, directionHorizontal).start,
        getLine(this.measurementRight, directionHorizontal).end,
        color,
        0.01,
        getLine(this.measurementRight, directionHorizontal).direction,
        0.01
      );
    }

    this.setLineCenters();
  }

  /**
   * Updates the labels with the distance of the line
   * aswell as the position of the label.
   */
  setLineCenters() {
    if (this.lines) {
      this.lineCenters = getLineCenters(this.lines, scale);
      const points = getInnerLineCenterPoints(this.lineCenters, this.coreApi);
      this.measurementsEntity.addComponent(new DistanceLineCenterComponent(points));
    }
  }

  /**
   * Runs when you release mouse press from the selected entity.
   * Removes both lines and labels from the scene.
   */
  onRemoveLines() {
    this.lineCenters = [];
    this.measurementsEntity.removeComponent(DistanceLineCenterComponent.id);

    Object.values(this.lines).forEach(line => {
      line.dispose();
    });

    this.lines = {};
  }

  getMeasurementToNextEntity(
    entity: Entity,
    room: Entity,
    direction: Direction,
    measureCloserToWall?: boolean
  ): InnerMeasurement | null {
    const directionLocalSpace = directionMap.get(direction);
    const entityTransform = entity.getComponent<WorldTransformComponent>(
      ComponentIds.WORLD_TRANSFORM_COMPONENT_ID
    )?.transform;

    const entityDepth =
      entity.getComponent<CommunicationSizeComponent>(ComponentIds.COMMUNICATION_SIZE_COMPONENT_ID)
        ?.size.depth || 0;

    if (entityTransform) {
      const directionWorldSpace = [directionToTop, directionToBottom].includes(directionLocalSpace)
        ? directionLocalSpace
        : entityTransform.transformVector(directionLocalSpace);

      const positionWorldSpace = measureCloserToWall
        ? entityTransform.position.plus(
            entityTransform.forward.times(entityDepth / 2 - 10) // 1 cm from wall
          )
        : entityTransform.position;

      const ray = new Ray(positionWorldSpace, directionWorldSpace);
      const outerHit = RaycastUtils.raycast(ray, room, entity);

      if (outerHit) {
        const epsilonOffset = directionWorldSpace.times(0.1);
        const reverseRayOrigin = outerHit.point.plus(epsilonOffset);
        const reverseRay = new Ray(reverseRayOrigin, directionWorldSpace.unaryMinus());

        const selfHit = RaycastUtils.raycast(reverseRay, entity);

        if (selfHit) {
          return new InnerMeasurement(selfHit, outerHit);
          /*{
            start: selfHit,
            end: outerHit,
            distance: selfHit.point.distanceTo(outerHit.point)
          };*/
        }
      }
    }

    return null;
  }
}
